/**
 * Favoriler, Ayarlar ekranlari ve hesap paneli.
 *
 * Hesap paneli (profil ikonu): profil bilgisi + zaman dilimi secimi +
 * bildirimler + Fiyat & Sinyal Alarmlari (kullanici istegi: bu ucu Ayarlar
 * sekmesinden BURAYA tasindi, Ayarlar'da tekrar EDILMEZ). Islem modu /
 * ACIL DURDUR / paper portfoy / pozisyonlar / denetim kaydi ise (ayrı bir
 * kullanici istegiyle) arayuzden tamamen KALDIRILDI - bu kontrollerin
 * backend uc noktalari (bkz. api/app.py) DOKUNULMADAN duruyor.
 */

import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { api } from "../api/client";
import type { Announcement, AuthUser, Settings, SignalRow } from "../api/types";
import type { PriceAlert } from "../lib/alerts";
import { dateTimeWithYear, price } from "../lib/format";
import { PushSetting } from "./PushSetting";
import { SignalList } from "./SignalScreen";
import { Card, Empty, IconClose, IconTheme, IconUser, Row, Setting, Switch } from "./Ui";

/* ========================================================================== */
/* Favoriler                                                                  */
/* ========================================================================== */
export function FavoritesScreen({
  favorites,
  rows,
  loading,
  timeframe,
  onSelect,
}: {
  favorites: string[];
  rows: SignalRow[];
  loading: boolean;
  timeframe: string;
  onSelect: (symbol: string) => void;
}) {
  const tracked = rows.filter((row) => favorites.includes(row.symbol));
  return (
    <>
      <h2 className="section-title">Favoriler</h2>
      <div className="status-line">
        <span>
          {favorites.length} elimde var işaretli sembol · {timeframe.toUpperCase()}
        </span>
      </div>

      {favorites.length === 0 ? (
        <Empty
          title="Takip listesi boş"
          detail="Bir coin'in detayını açıp 'Elimde Var' ile buraya ekleyebilirsiniz - Düşüş sinyalleri o zaman çıkış noktası olarak vurgulanır."
        />
      ) : (
        <>
          <SignalList
            rows={tracked}
            timeframe={timeframe}
            loading={loading}
            onSelect={onSelect}
            holdings={favorites}
            emptyTitle="Takip ettiklerinizde sinyal yok"
            emptyDetail="Favori sembollerde bu zaman diliminde yürürlükte onaylanmış sinyal bulunmuyor."
          />
          <Card title="Takip listesi">
            <div className="btn-row">
              {favorites.map((symbol) => (
                <button
                  key={symbol}
                  type="button"
                  className="btn"
                  onClick={() => onSelect(symbol)}
                >
                  {symbol}
                </button>
              ))}
            </div>
          </Card>
        </>
      )}
    </>
  );
}

/* ========================================================================== */
/* Ayarlar                                                                    */
/* ========================================================================== */
export function SettingsScreen({ settings }: { settings: Settings | null }) {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [pinnedNote, setPinnedNote] = useState<Announcement | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [nextAnnouncement, nextPinnedNote] = await Promise.all([
          api.announcement(),
          api.pinnedNote(),
        ]);
        if (!cancelled) {
          setAnnouncement(nextAnnouncement);
          setPinnedNote(nextPinnedNote);
        }
      } catch {
        /* duyuru/sabit bilgi gosterilemezse sayfa yine de acilir */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!settings) {
    return <Empty title="Duyurular yükleniyor…" />;
  }

  return (
    <>
      <h2 className="section-title">Duyurular</h2>

      <Card title="Sistem Bilgisi">
        <p className="note" style={{ marginBottom: 10 }}>
          <strong>{settings.signals.rule_tr}.</strong> Kural sabittir ve arayüzden
          gevşetilemez. Isınma için sembol başına {settings.signals.bar_count} kapanmış
          bar okunur; sonrasında seriler canlı akışla güncellenir.
        </p>
        <p className="note">
          Uygun <strong>tüm</strong> Binance Spot pariteleri taranır; sabit bir sembol
          sınırı yoktur.
        </p>
      </Card>

      {pinnedNote && pinnedNote.text ? (
        <Card title="Sabit Bilgi">
          <p className="note" style={{ whiteSpace: "pre-wrap" }}>
            {pinnedNote.text}
          </p>
        </Card>
      ) : null}

      {announcement && announcement.text ? (
        <Card title="Duyuru">
          <p className="note" style={{ whiteSpace: "pre-wrap" }}>
            {announcement.text}
          </p>
        </Card>
      ) : null}
    </>
  );
}

/**
 * Telefondaki "Menü" sayfasi - alt seride sigmayan sekmeler + hesap/tema
 * kisayollari. Alt serit referans tasarimdaki gibi 4 ana sekme + Menü ile
 * sinirlidir; 8 sekme telefon genisliginde okunmuyordu. GENIS EKRANDA HIC
 * KULLANILMAZ (orada sol kenar cubugu tum sekmeleri gosterir).
 */
export function MobileMenuSheet({
  items,
  dotFor = null,
  activeTab,
  onSelect,
  onClose,
  onAccount,
  theme,
  onThemeToggle,
}: {
  items: { key: string; label: string; icon: ReactNode; category?: string }[];
  /** Bu sekmenin simgesinde kirmizi nokta (ör. elimdeki coin'e Dusus sinyali). */
  dotFor?: string | null;
  activeTab: string;
  onSelect: (key: string) => void;
  onClose: () => void;
  onAccount: () => void;
  theme: "light" | "dark";
  onThemeToggle: () => void;
}) {
  return (
    <div
      className="sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Menü"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet">
        <header className="sheet-header">
          <div className="sheet-title">Menü</div>
          <button type="button" className="icon-btn sheet-close" onClick={onClose} aria-label="Kapat">
            <IconClose />
          </button>
        </header>
        <div className="sheet-body">
          <div className="menu-list">
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                className="menu-item"
                data-category={item.category}
                aria-current={activeTab === item.key ? "page" : undefined}
                onClick={() => {
                  onSelect(item.key);
                  onClose();
                }}
              >
                <span className="menu-item-icon">
                  {item.icon}
                  {item.key === dotFor ? <span className="nav-dot" aria-hidden="true" /> : null}
                </span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          <div className="menu-list" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="menu-item"
              onClick={() => {
                onAccount();
                onClose();
              }}
            >
              <span className="menu-item-icon">
                <IconUser />
              </span>
              <span>Hesabım & Ayarlar</span>
            </button>
            <button type="button" className="menu-item" onClick={onThemeToggle}>
              <span className="menu-item-icon">
                <IconTheme />
              </span>
              <span>{theme === "dark" ? "Açık temaya geç" : "Karanlık temaya geç"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Ust seritteki (Ticker.tsx) coin listesi - sunucu tarafinda kalici
 * (WatchlistStore.ticker_symbols, "Elimde Var" favorilerinden AYRI bir
 * liste - biri "elimde ne var" sorusuna, digeri "hangi coini genel
 * piyasa gostergesi olarak izlemek istiyorum" sorusuna cevap verir).
 * Bos birakilirsa backend varsayilana (BTC/ETH/BNB/SOL/XRP/DOGE) doner.
 */
function TickerSymbolsCard({
  symbols,
  onChange,
  busy,
}: {
  symbols: string[];
  onChange: (next: string[]) => void;
  busy: boolean;
}) {
  const [input, setInput] = useState("");

  const add = () => {
    const cleaned = input.trim().toUpperCase().replace(/\s+/g, "");
    if (!cleaned) return;
    const symbol = cleaned.endsWith("USDT") ? cleaned : `${cleaned}USDT`;
    setInput("");
    if (symbols.includes(symbol)) return;
    onChange([...symbols, symbol]);
  };

  return (
    <Card title="Üst Şerit">
      <p className="note" style={{ marginBottom: 10 }}>
        Sayfanın en üstündeki fiyat şeridinde hangi coinler görünsün seç. Boş bırakılırsa
        varsayılan (BTC, ETH, BNB, SOL, XRP, DOGE) gösterilir.
      </p>
      {symbols.length > 0 ? (
        <div className="chips" style={{ margin: 0, paddingInline: 0, marginBottom: 10 }}>
          {symbols.map((symbol) => (
            <button
              key={symbol}
              type="button"
              className="chip"
              aria-pressed={true}
              disabled={busy}
              onClick={() => onChange(symbols.filter((item) => item !== symbol))}
              aria-label={`${symbol} şeritten kaldır`}
              title="Kaldırmak için tıkla"
            >
              {symbol.replace(/USDT$/, "")} ✕
            </button>
          ))}
        </div>
      ) : (
        <p className="note" style={{ marginBottom: 10 }}>
          Şu an varsayılan liste kullanılıyor.
        </p>
      )}
      <div className="btn-row">
        <input
          type="text"
          className="input"
          style={{ flex: "1 1 160px", maxWidth: "none" }}
          placeholder="Sembol ekle (örn. BTC, ADA...)"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
          disabled={busy}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={add}
          disabled={busy || !input.trim()}
        >
          Ekle
        </button>
      </div>
    </Card>
  );
}

/**
 * Fiyat & Sinyal Alarmlari - "Elimde Var" bildiriminden AYRIDIR: o TUM
 * favori sembolleri izler, bu ise kullanicinin sectigi TEK bir sembol icin
 * belirli zaman dilim(ler)inde SAT sinyalini ve/veya bir fiyat hedefini
 * izler (bkz. lib/alerts.ts, App.tsx'teki kontrol dongusu).
 */
function AlertsCard({
  alerts,
  availableTimeframes,
  onAdd,
  onRemove,
}: {
  alerts: PriceAlert[];
  availableTimeframes: string[];
  onAdd: (input: { symbol: string; sellTimeframes: string[]; priceTarget: number | null }) => void;
  onRemove: (id: string) => void;
}) {
  const [symbol, setSymbol] = useState("");
  const [selectedTfs, setSelectedTfs] = useState<string[]>([]);
  const [priceText, setPriceText] = useState("");

  const canAdd = symbol.trim().length >= 2 && (selectedTfs.length > 0 || priceText.trim() !== "");

  const submit = () => {
    if (!canAdd) return;
    const priceTarget = priceText.trim() === "" ? null : Number(priceText);
    onAdd({
      symbol: symbol.trim().toUpperCase(),
      sellTimeframes: selectedTfs,
      priceTarget: priceTarget != null && Number.isFinite(priceTarget) ? priceTarget : null,
    });
    setSymbol("");
    setSelectedTfs([]);
    setPriceText("");
  };

  return (
    <Card title="Fiyat & Sinyal Alarmları">
      <p className="note" style={{ marginBottom: 10 }}>
        Belirli bir sembol için Düşüş sinyali geldiğinde veya fiyat bir hedefe ulaştığında ayrı,
        öncelikli bir bildirim alırsınız (bkz. "Elimde Var" bildirimleri gibi). Hesabında
        saklanır; tüm cihazlarında görünür. "Telefona bildirim" açıksa site kapalıyken de gelir.
      </p>

      {alerts.length > 0 ? (
        <div className="rows" style={{ marginBottom: 12 }}>
          {alerts.map((alert) => (
            <div className="row" key={alert.id}>
              <span className="row-label">
                <strong>{alert.symbol}</strong>
                <br />
                <span className="muted">
                  {[
                    alert.sellTimeframes.length > 0
                      ? `${alert.sellTimeframes.map((tf) => tf.toUpperCase()).join("/")} Düşüş`
                      : null,
                    alert.priceTarget != null
                      ? `fiyat ${alert.priceDirection === "below" ? "≤" : "≥"} ${price(alert.priceTarget)}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <button
                type="button"
                className="row-icon"
                onClick={() => onRemove(alert.id)}
                aria-label={`${alert.symbol} alarmını sil`}
              >
                <IconClose />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="note" style={{ marginBottom: 12 }}>
          Henüz alarm tanımlanmadı.
        </p>
      )}

      <div className="alert-form">
        <input
          type="text"
          className="input"
          placeholder="Sembol (örn. FFUSDT)"
          value={symbol}
          onChange={(event) => setSymbol(event.target.value)}
        />
        <div className="chips" style={{ margin: 0, paddingInline: 0 }}>
          {availableTimeframes.map((tf) => {
            const on = selectedTfs.includes(tf);
            return (
              <button
                key={tf}
                type="button"
                className="chip"
                aria-pressed={on}
                onClick={() =>
                  setSelectedTfs((current) =>
                    on ? current.filter((item) => item !== tf) : [...current, tf],
                  )
                }
              >
                {tf.toUpperCase()} Düşüş
              </button>
            );
          })}
        </div>
        <input
          type="number"
          inputMode="decimal"
          step="any"
          className="input"
          placeholder="Fiyat hedefi (opsiyonel)"
          value={priceText}
          onChange={(event) => setPriceText(event.target.value)}
        />
        <button type="button" className="btn" disabled={!canAdd} onClick={submit}>
          Alarm Ekle
        </button>
      </div>
    </Card>
  );
}

/* ========================================================================== */
/* Hesap paneli                                                               */
/* ========================================================================== */
export function AccountSheet({
  user,
  settings,
  busy,
  alerts,
  onPatch,
  onAddAlert,
  onRemoveAlert,
  onClose,
  onLogout,
}: {
  user: AuthUser;
  settings: Settings | null;
  busy: boolean;
  alerts: PriceAlert[];
  onPatch: (body: Record<string, unknown>) => void;
  onAddAlert: (input: { symbol: string; sellTimeframes: string[]; priceTarget: number | null }) => void;
  onRemoveAlert: (id: string) => void;
  onClose: () => void;
  onLogout: () => void;
}) {
  const enabled = settings?.scan.timeframes ?? [];
  const available = settings?.signals.available_timeframes ?? [];

  return (
    <div
      className="sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Hesabım"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet">
        <header className="sheet-header">
          <div>
            <div className="sheet-title">Hesabım</div>
          </div>
          <button type="button" className="icon-btn sheet-close" onClick={onClose} aria-label="Kapat">
            <IconClose />
          </button>
        </header>

        <div className="sheet-body">
          <Card>
            <div className="rows">
              <Row label="Ad Soyad">
                {user.first_name} {user.last_name}
              </Row>
              <Row label="E-posta">{user.email}</Row>
              <Row label="Üyelik Başlangıcı">
                {user.membership_start == null ? "Sınırsız" : dateTimeWithYear(user.membership_start)}
              </Row>
              <Row label="Üyelik Bitişi">
                {user.membership_end == null ? "Sınırsız" : dateTimeWithYear(user.membership_end)}
              </Row>
              {user.is_admin ? <Row label="Rol">Yönetici</Row> : null}
            </div>
            <div className="btn-row" style={{ marginTop: 10 }}>
              <button type="button" className="btn btn-danger" onClick={onLogout} disabled={busy}>
                Çıkış Yap
              </button>
            </div>
          </Card>

          {settings ? (
            <>
              {/* Sistem geneli: tarayiciyi yeniden kurar ve TUM uyeleri
                  etkiler - yalnizca yonetici gorur (sunucu da 403 doner). */}
              {settings.can_edit_system ? (
              <Card title="Zaman dilimleri">
                <p className="note" style={{ marginBottom: 10 }}>
                  Ana ekranda gösterilecek zaman dilimleri. En az bir tane seçili olmalıdır.
                </p>
                <div className="chips" style={{ margin: 0, paddingInline: 0 }}>
                  {available.map((timeframe) => {
                    const on = enabled.includes(timeframe);
                    return (
                      <button
                        key={timeframe}
                        type="button"
                        className="chip"
                        aria-pressed={on}
                        disabled={busy}
                        onClick={() => {
                          const next = on
                            ? enabled.filter((item) => item !== timeframe)
                            : [
                                ...available.filter(
                                  (item) => enabled.includes(item) || item === timeframe,
                                ),
                              ];
                          if (next.length === 0) return;
                          onPatch({ timeframes: next });
                        }}
                      >
                        {timeframe.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </Card>
              ) : null}

              <Card title="Sinyal kuralı">
                <Setting
                  name="Yalnızca taze sinyaller"
                  hint="Belirtilen bar sayısından eski sinyaller listelenmez."
                >
                  <select
                    className="select"
                    value={settings.signals.max_signal_age_bars ?? 0}
                    disabled={busy}
                    onChange={(event) => {
                      const value = Number(event.target.value);
                      onPatch(
                        value === 0
                          ? { clear_signal_age_limit: true }
                          : { max_signal_age_bars: value },
                      );
                    }}
                  >
                    <option value={0}>Sınırsız</option>
                    {[5, 10, 20, 50, 100].map((value) => (
                      <option key={value} value={value}>
                        Son {value} bar
                      </option>
                    ))}
                  </select>
                </Setting>
              </Card>

              <Card title="Bildirimler">
                <Setting name="Yeni sinyal bildirimi" hint="Yeni onaylanmış sinyalde uyarı göster.">
                  <Switch
                    checked={settings.notifications.new_signals}
                    label="Yeni sinyal bildirimi"
                    onChange={(next) => onPatch({ notify_new_signals: next })}
                  />
                </Setting>
                <Setting name="Ses" hint="Bildirimde kısa bir uyarı sesi çal.">
                  <Switch
                    checked={settings.notifications.sound}
                    label="Bildirim sesi"
                    onChange={(next) => onPatch({ notify_sound: next })}
                  />
                </Setting>
                <PushSetting settings={settings} onPatch={onPatch} busy={busy} />
                <p className="note">
                  Yeni sinyal bildirimi ve ses <strong>site açıkken</strong> çalışır. Elimde Var
                  coin'lerine gelen Düşüş sinyalleri ve alarmların ise "Telefona bildirim" açıksa{" "}
                  <strong>site kapalıyken de</strong> gelir.
                </p>
              </Card>

              <TickerSymbolsCard
                symbols={settings.ticker_symbols}
                busy={busy}
                onChange={(next) => onPatch({ ticker_symbols: next })}
              />

              <AlertsCard
                alerts={alerts}
                availableTimeframes={available}
                onAdd={onAddAlert}
                onRemove={onRemoveAlert}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
